'use client';

import { useEffect, useRef, useState, type RefObject } from 'react';
import { shouldDismissCopilotProcessingWorkingLayer } from './copilotProcessingWorkingLayerDismiss';

type UseCopilotProcessingWorkingLayerDismissParams = {
  isProcessing: boolean;
};

type UseCopilotProcessingWorkingLayerDismissResult = {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  isWorkingLayerDismissed: boolean;
};

export function useCopilotProcessingWorkingLayerDismiss({
  isProcessing,
}: UseCopilotProcessingWorkingLayerDismissParams): UseCopilotProcessingWorkingLayerDismissResult {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const anchorScrollTopRef = useRef(0);
  const isWorkingLayerDismissedRef = useRef(false);
  const [isWorkingLayerDismissed, setIsWorkingLayerDismissed] = useState(false);

  useEffect(() => {
    isWorkingLayerDismissedRef.current = false;
    setIsWorkingLayerDismissed(false);
  }, [isProcessing]);

  useEffect(() => {
    if (!isProcessing) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) {
      return;
    }

    anchorScrollTopRef.current = scrollContainer.scrollTop;

    const handleScroll = (event: Event) => {
      const shouldDismiss = shouldDismissCopilotProcessingWorkingLayer({
        anchorScrollTop: anchorScrollTopRef.current,
        currentScrollTop: scrollContainer.scrollTop,
        isTrusted: event.isTrusted,
        isAlreadyDismissed: isWorkingLayerDismissedRef.current,
      });

      if (!shouldDismiss) {
        return;
      }

      isWorkingLayerDismissedRef.current = true;
      setIsWorkingLayerDismissed(true);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [isProcessing]);

  return {
    scrollContainerRef,
    isWorkingLayerDismissed,
  };
}
